import React, { useState, useEffect, useRef } from "react";
import AdminLayout from "../../components/admin/AdminLayout.jsx";
import { API_BASE, apiFetch, authHeader } from "../../utils/api";
import "./AdminAboutUs.css";

/* ─────────────────────────────────
   Helpers
   ───────────────────────────────── */
/* webpack require.context for about-us hero images */
let ABOUT_IMAGES = {};
try {
  const ctx = require.context(
    "../../assets/uploads/about",
    false,
    /\.(png|jpe?g|gif|webp|svg)$/i
  );
  ABOUT_IMAGES = ctx.keys().reduce((map, key) => {
    map[key.replace(/^\.\//, "")] = ctx(key);
    return map;
  }, {});
} catch (_) {
  /* outside CRA — fall back to API URL */
}

function aboutImg(filename) {
  if (!filename) return null;
  return ABOUT_IMAGES[filename] ?? `${API_BASE}/uploads/about/${filename}`;
}

const EMPTY_FORM = {
  title:              "About Us",
  content:            "",
  mission_statement:  "",
  vision:             "",
  objectives:         "",
  core_values:        "",
  meta_description:   "",
  active:             1,
  hero_image:         null,  // new File upload
};

/* ─────────────────────────────────
   Character counter
   ───────────────────────────────── */
function CharCount({ val, max }) {
  const len = (val || "").length;
  const over = max && len > max;
  return (
    <span className={`aau-char-count ${over ? "aau-char-count--over" : ""}`}>
      {len}{max ? ` / ${max}` : ""}
    </span>
  );
}

/* ─────────────────────────────────
   Section Header
   ───────────────────────────────── */
function SectionHead({ title, desc }) {
  return (
    <div className="aau-section__head">
      <h3 className="aau-section__title">{title}</h3>
      {desc && <p className="aau-section__desc">{desc}</p>}
    </div>
  );
}

/* ─────────────────────────────────
   Textarea Field
   ───────────────────────────────── */
function TextareaField({ label, hint, value, onChange, rows = 5, maxLen, placeholder, required }) {
  return (
    <div className="aau-field">
      <div className="aau-field__top">
        <label className="aau-field__label">
          {label}
          {required && <span className="aau-req">*</span>}
        </label>
        {maxLen && <CharCount val={value} max={maxLen} />}
      </div>
      {hint && <p className="aau-field__hint">{hint}</p>}
      <textarea
        className="aau-textarea"
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || ""}
        maxLength={maxLen || undefined}
      />
    </div>
  );
}

/* ─────────────────────────────────
   Status indicator dot
   ───────────────────────────────── */
function StatusPill({ active }) {
  return (
    <span className={`aau-status-pill ${Number(active) === 1 ? "aau-status-pill--active" : "aau-status-pill--inactive"}`}>
      {Number(active) === 1 ? "● Active" : "○ Inactive"}
    </span>
  );
}

/* ═══════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════ */
export default function AdminAboutUs() {
  const [record,   setRecord]   = useState(null);
  const [form,     setForm]     = useState(EMPTY_FORM);
  const [preview,  setPreview]  = useState(null);   // hero image preview URL
  const [clearImg, setClearImg] = useState(false);
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState("");
  const [success,  setSuccess]  = useState("");
  const [dirty,    setDirty]    = useState(false);
  const fileRef = useRef(null);

  /* ── Load record ── */
  useEffect(() => {
    apiFetch("/api/admin/about-us")
      .then((data) => {
        setRecord(data);
        populateForm(data);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  function populateForm(data) {
    setForm({
      title:             data.title             || "About Us",
      content:           data.content           || "",
      mission_statement: data.mission_statement || "",
      vision:            data.vision            || "",
      objectives:        data.objectives        || "",
      core_values:       data.core_values       || "",
      meta_description:  data.meta_description  || "",
      active:            Number(data.active) === 1 ? 1 : 0,
      hero_image:        null,
    });
    setPreview(data.hero_image ? aboutImg(data.hero_image) : null);
    setClearImg(false);
    setDirty(false);
  }

  const set = (key, val) => {
    setForm((f) => ({ ...f, [key]: val }));
    setDirty(true);
    setSuccess("");
  };

  /* ── Hero image handlers ── */
  const handleFile = (file) => {
    if (!file) return;
    set("hero_image", file);
    setClearImg(false);
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleClearImage = () => {
    set("hero_image", null);
    setPreview(null);
    setClearImg(true);
    if (fileRef.current) fileRef.current.value = "";
  };

  /* ── Discard ── */
  const handleReset = () => {
    if (!record) return;
    populateForm(record);
    setError("");
    setSuccess("");
  };

  /* ── Save ── */
  const handleSave = async () => {
    setError("");
    setSuccess("");
    if (!form.title.trim()) return setError("Page title is required.");

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("title",             form.title.trim());
      fd.append("content",           form.content           || "");
      fd.append("mission_statement", form.mission_statement || "");
      fd.append("vision",            form.vision            || "");
      fd.append("objectives",        form.objectives        || "");
      fd.append("core_values",       form.core_values       || "");
      fd.append("meta_description",  form.meta_description  || "");
      fd.append("active",            form.active);
      if (form.hero_image) fd.append("hero_image", form.hero_image);
      if (clearImg)        fd.append("clear_image", "1");

      const updated = await apiFetch("/api/admin/about-us", {
        method: "PUT",
        body:   fd,
      });

      setRecord(updated);
      populateForm(updated);
      setSuccess("About Us page saved successfully.");
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  /* ── Skeleton ── */
  if (loading) {
    return (
      <AdminLayout pageTag="CONTENT" pageTitle="About Us">
        <div className="aau-skeleton" />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout pageTag="CONTENT" pageTitle="About Us">

      {/* ── Intro bar ── */}
      <div className="aau-intro-bar">
        <div className="aau-intro-bar__left">
          <span className="aau-intro-bar__icon">📄</span>
          <div>
            <h2 className="aau-intro-bar__title">About Us Page</h2>
            <p className="aau-intro-bar__sub">
              Manage the content displayed on your About Us page — story, mission, values and more.
            </p>
          </div>
        </div>
        <div className="aau-intro-bar__right">
          {dirty && <span className="aau-unsaved">● Unsaved changes</span>}
          <StatusPill active={form.active} />
        </div>
      </div>

      {/* ── Alerts ── */}
      {error   && <div className="aau-alert aau-alert--error">⚠ {error}</div>}
      {success && <div className="aau-alert aau-alert--success">✓ {success}</div>}

      {/* ── Two-column layout ── */}
      <div className="aau-layout">

        {/* ════════ MAIN FORM PANEL ════════ */}
        <div className="aau-form-panel">

          {/* ── Section: Page Identity ── */}
          <div className="aau-section">
            <SectionHead
              title="Page Identity"
              desc="The page title and SEO meta description used in search engine results."
            />

            <div className="aau-field">
              <div className="aau-field__top">
                <label className="aau-field__label">
                  Page Title <span className="aau-req">*</span>
                </label>
              </div>
              <input
                type="text"
                className="aau-input"
                value={form.title}
                onChange={(e) => set("title", e.target.value)}
                placeholder="About Us"
              />
            </div>

            <TextareaField
              label="Meta Description"
              hint="Shown in Google search results — keep between 120 and 160 characters."
              value={form.meta_description}
              onChange={(v) => set("meta_description", v)}
              rows={3}
              maxLen={700}
              placeholder="A brief description of your company shown in search engine results…"
            />
          </div>

          {/* ── Section: Hero Image ── */}
          <div className="aau-section">
            <SectionHead
              title="Hero Image"
              desc="The large banner image displayed at the top of the About Us page."
            />

            <div className="aau-image-upload-wrap">
              <div
                className={`aau-image-drop ${preview ? "aau-image-drop--has-image" : ""}`}
                onClick={() => fileRef.current?.click()}
              >
                {preview ? (
                  <img src={preview} alt="Hero preview" className="aau-image-drop__preview" />
                ) : (
                  <div className="aau-image-drop__placeholder">
                    <span className="aau-image-drop__icon">🖼</span>
                    <span className="aau-image-drop__text">Click to upload hero image</span>
                    <span className="aau-image-drop__sub">PNG, JPG, WebP — max 10 MB</span>
                  </div>
                )}
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={(e) => handleFile(e.target.files[0])}
                />
              </div>

              <div className="aau-image-actions">
                <button
                  type="button"
                  className="aau-image-btn"
                  onClick={() => fileRef.current?.click()}
                >
                  {preview ? "Change Image" : "Upload Image"}
                </button>
                {preview && (
                  <button
                    type="button"
                    className="aau-image-btn aau-image-btn--remove"
                    onClick={handleClearImage}
                  >
                    Remove
                  </button>
                )}
              </div>
              <p className="aau-field__hint" style={{ marginTop: 6 }}>
                Images are saved to <code>src/assets/uploads/about/</code>
              </p>
            </div>
          </div>

          {/* ── Section: Main Content ── */}
          <div className="aau-section">
            <SectionHead
              title="Main Content"
              desc="The primary body text of the About Us page — your company story, history, and overview."
            />
            <TextareaField
              label="Content"
              value={form.content}
              onChange={(v) => set("content", v)}
              rows={8}
              placeholder="Tell your company story here — who you are, how you started, what you do…"
            />
          </div>

          {/* ── Section: Mission & Vision ── */}
          <div className="aau-section">
            <SectionHead
              title="Mission & Vision"
              desc="Your company's purpose and long-term direction."
            />
            <div className="aau-two-col">
              <TextareaField
                label="Mission Statement"
                hint="What your company does and who it serves."
                value={form.mission_statement}
                onChange={(v) => set("mission_statement", v)}
                rows={5}
                placeholder="Our mission is to…"
              />
              <TextareaField
                label="Vision"
                hint="Where your company is headed in the future."
                value={form.vision}
                onChange={(v) => set("vision", v)}
                rows={5}
                placeholder="We envision a world where…"
              />
            </div>
          </div>

          {/* ── Section: Values & Objectives ── */}
          <div className="aau-section aau-section--last">
            <SectionHead
              title="Values & Objectives"
              desc="What your company stands for and the goals you're working toward."
            />
            <div className="aau-two-col">
              <TextareaField
                label="Core Values"
                hint="The principles that guide how you work."
                value={form.core_values}
                onChange={(v) => set("core_values", v)}
                rows={6}
                placeholder={"Integrity\nQuality\nCustomer-first\nInnovation"}
              />
              <TextareaField
                label="Objectives"
                hint="Specific goals or outcomes your company is working toward."
                value={form.objectives}
                onChange={(v) => set("objectives", v)}
                rows={6}
                placeholder={"Deliver best-in-class service\nExpand across South Africa\nBuild lasting client relationships"}
              />
            </div>
          </div>

          {/* ── Form Footer ── */}
          <div className="aau-form-footer">
            <div className="aau-form-footer__left">
              <label className="aau-check">
                <input
                  type="checkbox"
                  checked={Number(form.active) === 1}
                  onChange={(e) => set("active", e.target.checked ? 1 : 0)}
                />
                <span>Page Active (visible on site)</span>
              </label>
            </div>
            <div className="aau-form-footer__right">
              <button
                className="aau-btn aau-btn--cancel"
                onClick={handleReset}
                disabled={saving || !dirty}
              >
                Discard Changes
              </button>
              <button
                className="aau-btn aau-btn--save"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>

        {/* ════════ SIDEBAR ════════ */}
        <div className="aau-sidebar">

          {/* Page status card */}
          <div className="aau-sidebar-card">
            <div className="aau-sidebar-card__header">
              <span className="aau-sidebar-card__tag">PAGE STATUS</span>
            </div>
            <div className="aau-sidebar-card__body">
              <div className="aau-status-row">
                <span className="aau-status-row__label">Visibility</span>
                <StatusPill active={form.active} />
              </div>
              {record?.updated_at && (
                <div className="aau-status-row">
                  <span className="aau-status-row__label">Last Saved</span>
                  <span className="aau-status-row__val">
                    {new Date(record.updated_at).toLocaleString("en-ZA", {
                      dateStyle: "medium",
                      timeStyle: "short",
                    })}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Content checklist */}
          <div className="aau-sidebar-card">
            <div className="aau-sidebar-card__header">
              <span className="aau-sidebar-card__tag">CONTENT CHECKLIST</span>
            </div>
            <div className="aau-sidebar-card__body">
              {[
                { label: "Page Title",        filled: !!form.title.trim() },
                { label: "Meta Description",  filled: !!form.meta_description.trim() },
                { label: "Hero Image",        filled: !!preview },
                { label: "Main Content",      filled: !!form.content.trim() },
                { label: "Mission Statement", filled: !!form.mission_statement.trim() },
                { label: "Vision",            filled: !!form.vision.trim() },
                { label: "Core Values",       filled: !!form.core_values.trim() },
                { label: "Objectives",        filled: !!form.objectives.trim() },
              ].map(({ label, filled }) => (
                <div key={label} className="aau-checklist-row">
                  <span className={`aau-checklist-dot ${filled ? "aau-checklist-dot--done" : ""}`}>
                    {filled ? "✓" : "○"}
                  </span>
                  <span className={`aau-checklist-label ${filled ? "" : "aau-checklist-label--empty"}`}>
                    {label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Tips */}
          <div className="aau-tips">
            <h4 className="aau-tips__title">💡 Tips</h4>
            <ul className="aau-tips__list">
              <li>Keep the meta description between 120–160 characters for best SEO results.</li>
              <li>Use bullet points or numbered lists in text fields — your frontend can style them.</li>
              <li>Hero images work best at 1920×600px or wider.</li>
              <li>Changes take effect immediately after saving.</li>
            </ul>
          </div>
        </div>

      </div>
    </AdminLayout>
  );
}